import React, { useEffect, useState } from 'react';
import { Modal, Button, Upload, message, Checkbox } from 'antd';
import { UploadOutlined, CloseOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { collection, getDocs, addDoc, deleteDoc, doc, query, where, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from '../../../firebaseConfig';
import { Carousel } from 'react-responsive-carousel';
import "react-responsive-carousel/lib/styles/carousel.min.css";
import './PhotoModal.less';

const { confirm } = Modal;

interface PhotoModalProps {
    isOpen: boolean;
    onClose: () => void;
    itemId: string;
}

const PhotoModal = ({ isOpen, onClose, itemId }: PhotoModalProps) => {
    const [photos, setPhotos] = useState<{ id: string, photoUrl: string, isMain: boolean }[]>([]);
    const [newPhoto, setNewPhoto] = useState<File | null>(null);
    const [mainPhotoId, setMainPhotoId] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen && itemId) {
            fetchPhotos();
        }
    }, [isOpen, itemId]);

    const fetchPhotos = async () => {
        try {
            const q = query(collection(db, "item-photos"), where("itemId", "==", itemId));
            const querySnapshot = await getDocs(q);
            const photosData = querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })) as { id: string, photoUrl: string, isMain: boolean }[];
            setPhotos(photosData);
            const mainPhoto = photosData.find(photo => photo.isMain);
            setMainPhotoId(mainPhoto ? mainPhoto.id : null);
        } catch (error) {
            console.error("Error fetching photos: ", error);
        }
    };

    const handleUpload = async () => {
        if (!newPhoto) return null;
        try {
            const storageRef = ref(storage, `item-photos/${itemId}/${newPhoto.name}`);
            const snapshot = await uploadBytes(storageRef, newPhoto);
            const photoUrl = await getDownloadURL(snapshot.ref);

            const newPhotoDoc = {
                itemId,
                photoUrl,
                isMain: photos.length === 0 // Set isMain to true if this is the first photo
            };

            const docRef = await addDoc(collection(db, "item-photos"), newPhotoDoc);
            return { id: docRef.id, ...newPhotoDoc };
        } catch (error) {
            console.error("Error uploading photo: ", error);
            message.error("Error uploading photo!");
            return null;
        }
    };

    const handleDeletePhoto = (photoId: string) => {
        confirm({
            title: 'Are you sure you want to delete this photo?',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk: async () => {
                try {
                    await deleteDoc(doc(db, "item-photos", photoId));
                    fetchPhotos();
                } catch (error) {
                    console.error("Error deleting photo: ", error);
                    message.error("Error deleting photo!");
                }
            }
        });
    };

    const handleSave = async () => {
        try {
            if (newPhoto) {
                const uploadedPhoto = await handleUpload();
                if (uploadedPhoto) {
                    setPhotos([...photos, uploadedPhoto]);
                    setNewPhoto(null);
                }
            }

            for (const photo of photos) {
                const photoDoc = doc(db, "item-photos", photo.id);
                await updateDoc(photoDoc, { isMain: photo.id === mainPhotoId });
            }

            message.success("Photos updated successfully!");
            handleClose();
        } catch (error) {
            console.error("Error updating photos: ", error);
            message.error("Error updating photos!");
        }
    };

    const handleClose = () => {
        setPhotos([]);
        setNewPhoto(null);
        setMainPhotoId(null);
        onClose();
    };

    const renderCustomArrowPrev = (onClickHandler: () => void, hasPrev: boolean, label: string) =>
        hasPrev && (
            <Button
                className="custom-arrow custom-arrow-prev"
                onClick={onClickHandler}
                icon={<LeftOutlined />}
            />
        );

    const renderCustomArrowNext = (onClickHandler: () => void, hasNext: boolean, label: string) =>
        hasNext && (
            <Button
                className="custom-arrow custom-arrow-next"
                onClick={onClickHandler}
                icon={<RightOutlined />}
            />
        );

    return (
        <Modal
            title="Photos"
            open={isOpen}
            onCancel={handleClose}
            footer={[
                <Upload
                    key="select-photo"
                    beforeUpload={file => {
                        setNewPhoto(file);
                        return false;
                    }}
                    onRemove={() => setNewPhoto(null)}
                    fileList={newPhoto ? [{ ...newPhoto, uid: 'temp-id' }] : []}
                    listType="picture"
                    maxCount={1}
                    className="upload-button"
                >
                    <Button icon={<UploadOutlined />}>Select Photo</Button>
                </Upload>,
                <Button key="close" onClick={handleClose}>Close</Button>,
                <Button key="save" type="primary" onClick={handleSave}>Save</Button>
            ]}
        >
            <div className="carousel-container">
                <Carousel
                    showArrows={true}
                    dynamicHeight={true}
                    renderArrowPrev={renderCustomArrowPrev}
                    renderArrowNext={renderCustomArrowNext}
                >
                    {photos.map(photo => (
                        <div key={photo.id} className="photo-carousel-item">
                            <img src={photo.photoUrl} alt="item" className="photo-image" />
                            <div className="photo-buttons">
                                <Button
                                    icon={<CloseOutlined />}
                                    onClick={() => handleDeletePhoto(photo.id)}
                                    shape="circle"
                                    className="delete-button"
                                />
                                <Checkbox
                                    className="main-checkbox"
                                    checked={photo.id === mainPhotoId}
                                    onChange={() => setMainPhotoId(photo.id)}
                                >
                                    Main
                                </Checkbox>
                            </div>
                        </div>
                    ))}
                </Carousel>
            </div>
        </Modal>
    );
};

export default PhotoModal;
